/**
 * FrictionLogDashboard — Phase 2a (with beta-readiness additions)
 * ───────────────────────────────────────────────────────────────
 * Read-only visibility over the four canonical Phase 1c sinks:
 *   • soft-enforcement suggestions  (dismissed / overridden / expired)
 *   • override outcomes             (negative re-reviews)
 *   • dispute events                (repeat disputes per contractor)
 *   • holdback ledger reversals     (repeat reversals per contractor)
 *
 * Beta additions:
 *   • Date-range filter (24h / 7d / 30d / all)
 *   • CSV export per section
 *   • "Mark reviewed" annotation (append-only, UX state only — does NOT
 *     mutate the underlying suggestion / dispute / holdback)
 *   • "Jump to job" deep-link into the Execution Timeline above
 */

import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, CheckCircle2, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";
import {
  indexLatestAcks, isAcknowledged, type FrictionAckSubjectKind,
} from "@/data/observability/frictionAcknowledgements";

type RangeKey = "24h" | "7d" | "30d" | "all";

const RANGE_LABEL: Record<RangeKey, string> = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

function rangeStart(range: RangeKey): number | null {
  if (range === "all") return null;
  const now = Date.now();
  if (range === "24h") return now - 24 * 60 * 60 * 1000;
  if (range === "7d") return now - 7 * 24 * 60 * 60 * 1000;
  return now - 30 * 24 * 60 * 60 * 1000;
}

function withinRange(iso: string | undefined, since: number | null): boolean {
  if (since === null) return true;
  if (!iso) return false;
  return new Date(iso).getTime() >= since;
}

function downloadCsv(filename: string, rows: ReadonlyArray<Record<string, string | number>>) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface RepeatRow {
  key: string;
  label: string;
  count: number;
}

function topRepeats<T>(
  items: ReadonlyArray<T>,
  keyFn: (t: T) => string,
  labelFn: (t: T) => string,
  max = 10,
): RepeatRow[] {
  const counts = new Map<string, { label: string; count: number }>();
  items.forEach((it) => {
    const k = keyFn(it);
    if (!k) return;
    const prev = counts.get(k);
    if (prev) prev.count += 1;
    else counts.set(k, { label: labelFn(it), count: 1 });
  });
  return Array.from(counts.entries())
    .map(([key, v]) => ({ key, label: v.label, count: v.count }))
    .filter((r) => r.count >= MIN_SAMPLE_FOR_DISPLAY)
    .sort((a, b) => b.count - a.count)
    .slice(0, max);
}

function Section({
  title,
  subtitle,
  onExport,
  children,
}: {
  title: string;
  subtitle: string;
  onExport?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-[11px] text-muted-foreground">{subtitle}</p>
        </div>
        {onExport && (
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onExport}>
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        )}
      </div>
      <Card className="p-0 overflow-hidden">{children}</Card>
    </div>
  );
}

function EmptyRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center text-xs text-muted-foreground py-6">
        {label}
      </TableCell>
    </TableRow>
  );
}

interface AckBtnProps {
  acknowledged: boolean;
  onClick: () => void;
}

function AckButton({ acknowledged, onClick }: AckBtnProps) {
  if (acknowledged) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        <CheckCircle2 className="h-3 w-3 text-success" /> Reviewed
      </Badge>
    );
  }
  return (
    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={onClick}>
      Mark reviewed
    </Button>
  );
}

interface Props {
  /** Optional callback to jump to a job in the Execution Timeline above. */
  onJumpToJob?: (jobId: string) => void;
}

export function FrictionLogDashboard({ onJumpToJob }: Props) {
  const _data = useAppData();
  const { contractors } = _data;
  const softSuggestions: any[] = (_data as any).softSuggestions ?? [];
  const overrideOutcomes: any[] = (_data as any).overrideOutcomes ?? [];
  const disputeEvents: any[] = (_data as any).disputeEvents ?? [];
  const holdbackLedger: any[] = (_data as any).holdbackLedger ?? [];
  const frictionAcks: any[] = (_data as any).frictionAcks ?? [];
  const acknowledgeFriction: ((...args: any[]) => void) = (_data as any).acknowledgeFriction ?? (() => {});
  const { role } = useRole();
  const reviewerId = role;

  const [range, setRange] = useState<RangeKey>("7d");
  const [hideReviewed, setHideReviewed] = useState(true);
  const [ackTarget, setAckTarget] = useState<{
    kind: FrictionAckSubjectKind;
    subjectId: string;
    label: string;
  } | null>(null);
  const [ackNote, setAckNote] = useState("");

  const since = rangeStart(range);
  const ackIndex = useMemo(() => indexLatestAcks(frictionAcks), [frictionAcks]);
  const contractorName = (id: string) => contractors.find((c) => c.id === id)?.companyName ?? id;

  const openAck = (kind: FrictionAckSubjectKind, subjectId: string, label: string) => {
    setAckTarget({ kind, subjectId, label });
    setAckNote("");
  };

  const submitAck = () => {
    if (!ackTarget) return;
    acknowledgeFriction({
      subjectKind: ackTarget.kind,
      subjectId: ackTarget.subjectId,
      acknowledgedBy: reviewerId,
      note: ackNote || undefined,
    });
    setAckTarget(null);
    setAckNote("");
  };

  // ── Soft-suggestion outcomes within range ──────────────────────────
  const dismissed = softSuggestions.filter(
    (s) => s.outcome === "dismissed" && withinRange(s.outcomeAt ?? s.suggestedAt, since),
  );
  const overridden = softSuggestions.filter(
    (s) => s.outcome === "overridden" && withinRange(s.outcomeAt ?? s.suggestedAt, since),
  );
  const expired = softSuggestions.filter(
    (s) => s.outcome === "ignored_expired" && withinRange(s.outcomeAt ?? s.suggestedAt, since),
  );

  const advisoryRows = [...dismissed, ...overridden]
    .filter((s) => !hideReviewed || !isAcknowledged(ackIndex, "suggestion", s.id))
    .sort((a, b) =>
      new Date(b.outcomeAt ?? b.suggestedAt).getTime()
      - new Date(a.outcomeAt ?? a.suggestedAt).getTime(),
    )
    .slice(0, 50);

  // ── Repeat disputes (within range) ─────────────────────────────────
  const repeatDisputes = useMemo(
    () =>
      topRepeats(
        disputeEvents.filter((d) => d.type === "open" && withinRange(d.createdAt, since)),
        (d) => d.contractorId,
        (d) => contractorName(d.contractorId),
      ),
    [disputeEvents, since, contractors],
  );

  // ── Repeat reversals (within range) ────────────────────────────────
  const repeatReversals = useMemo(
    () =>
      topRepeats(
        holdbackLedger.filter((h) => h.action === "reverse" && withinRange(h.createdAt, since)),
        (h) => h.contractorId,
        (h) => contractorName(h.contractorId),
      ),
    [holdbackLedger, since, contractors],
  );

  // ── Negative override outcomes (within range) ──────────────────────
  const negativeOverrides = overrideOutcomes
    .filter((o) => o.outcome === "negative" && withinRange(o.recordedAt, since))
    .filter((o) => !hideReviewed || !isAcknowledged(ackIndex, "override_outcome", `${o.jobId}:${o.observationId}`))
    .sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime())
    .slice(0, 50);

  // ── CSV exports ────────────────────────────────────────────────────
  const exportAdvisory = () =>
    downloadCsv(`friction-advisory-${range}.csv`, advisoryRows.map((s) => ({
      when: s.outcomeAt ?? s.suggestedAt,
      kind: s.kind,
      outcome: s.outcome,
      subject_kind: s.subjectKind,
      subject_id: s.subjectId,
      reason: s.outcomeReason ?? "",
      reviewed: isAcknowledged(ackIndex, "suggestion", s.id) ? "yes" : "no",
    })));

  const exportDisputes = () =>
    downloadCsv(`friction-repeat-disputes-${range}.csv`, repeatDisputes.map((r) => ({
      contractor_id: r.key,
      contractor_name: r.label,
      open_disputes: r.count,
    })));

  const exportReversals = () =>
    downloadCsv(`friction-repeat-reversals-${range}.csv`, repeatReversals.map((r) => ({
      contractor_id: r.key,
      contractor_name: r.label,
      reversals: r.count,
    })));

  const exportOverrides = () =>
    downloadCsv(`friction-negative-overrides-${range}.csv`, negativeOverrides.map((o) => ({
      when: o.recordedAt,
      job_id: o.jobId,
      reviewer_id: o.reviewerId,
      reason: o.reason,
      reviewed: isAcknowledged(ackIndex, "override_outcome", `${o.jobId}:${o.observationId}`) ? "yes" : "no",
    })));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold">FrictionLog Dashboard</h2>
          <p className="text-[11px] text-muted-foreground">
            Where the system creates operational resistance • read-only • repeats shown only at ≥{MIN_SAMPLE_FOR_DISPLAY} occurrences
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Range toggle (matches cockpit active-state language) */}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/30">
            {(["24h", "7d", "30d", "all"] as RangeKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setRange(k)}
                className={`px-2.5 py-1 text-[11px] rounded transition-colors ${
                  range === k
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={range === k}
              >
                {k === "all" ? "All" : k}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={() => setHideReviewed((v) => !v)}
            aria-pressed={hideReviewed}
          >
            {hideReviewed ? "Hide reviewed" : "Show reviewed"}
          </Button>
        </div>
      </div>

      {/* Headline counts (range-scoped) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Dismissed advisories</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5">{dismissed.length}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{RANGE_LABEL[range]}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Overridden recommendations</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5">{overridden.length}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{RANGE_LABEL[range]}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ignored / expired</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5">{expired.length}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{RANGE_LABEL[range]}</div>
        </Card>
      </div>

      {/* Recent dismissed/overridden suggestions */}
      <Section
        title="Recent advisory friction"
        subtitle={`Dismissed or overridden suggestions in ${RANGE_LABEL[range].toLowerCase()}`}
        onExport={advisoryRows.length ? exportAdvisory : undefined}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px]">When</TableHead>
              <TableHead className="text-[11px]">Kind</TableHead>
              <TableHead className="text-[11px]">Outcome</TableHead>
              <TableHead className="text-[11px]">Subject</TableHead>
              <TableHead className="text-[11px]">Reason</TableHead>
              <TableHead className="text-[11px] text-right">Review</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {advisoryRows.map((s) => {
              const acked = isAcknowledged(ackIndex, "suggestion", s.id);
              return (
                <TableRow key={s.id}>
                  <TableCell className="text-[11px] whitespace-nowrap text-muted-foreground">
                    {format(new Date(s.outcomeAt ?? s.suggestedAt), "MMM d, h:mm a")}
                  </TableCell>
                  <TableCell className="text-xs font-medium">{s.kind}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{s.outcome}</Badge></TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{s.subjectKind}: {s.subjectId}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{s.outcomeReason ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <AckButton
                      acknowledged={acked}
                      onClick={() => openAck("suggestion", s.id, `${s.kind} on ${s.subjectKind}:${s.subjectId}`)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {advisoryRows.length === 0 && (
              <EmptyRow colSpan={6} label="No advisory friction in this range." />
            )}
          </TableBody>
        </Table>
      </Section>

      {/* Repeat disputes */}
      <Section
        title="Repeat disputes"
        subtitle={`Contractors with ≥${MIN_SAMPLE_FOR_DISPLAY} disputes opened in ${RANGE_LABEL[range].toLowerCase()}`}
        onExport={repeatDisputes.length ? exportDisputes : undefined}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px]">Contractor</TableHead>
              <TableHead className="text-[11px] text-right">Open disputes</TableHead>
              <TableHead className="text-[11px] text-right">Review</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {repeatDisputes.map((r) => {
              const acked = isAcknowledged(ackIndex, "repeat_dispute", r.key);
              return (
                <TableRow key={r.key}>
                  <TableCell className="text-xs font-medium">{r.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                  <TableCell className="text-right">
                    <AckButton
                      acknowledged={acked}
                      onClick={() => openAck("repeat_dispute", r.key, `Repeat disputes — ${r.label}`)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {repeatDisputes.length === 0 && (
              <EmptyRow colSpan={3} label={`No contractor has reached ${MIN_SAMPLE_FOR_DISPLAY}+ disputes in this range.`} />
            )}
          </TableBody>
        </Table>
      </Section>

      {/* Repeat reversals */}
      <Section
        title="Repeat holdback reversals"
        subtitle={`Contractors with ≥${MIN_SAMPLE_FOR_DISPLAY} reversal entries in ${RANGE_LABEL[range].toLowerCase()}`}
        onExport={repeatReversals.length ? exportReversals : undefined}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px]">Contractor</TableHead>
              <TableHead className="text-[11px] text-right">Reversals</TableHead>
              <TableHead className="text-[11px] text-right">Review</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {repeatReversals.map((r) => {
              const acked = isAcknowledged(ackIndex, "repeat_reversal", r.key);
              return (
                <TableRow key={r.key}>
                  <TableCell className="text-xs font-medium">{r.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                  <TableCell className="text-right">
                    <AckButton
                      acknowledged={acked}
                      onClick={() => openAck("repeat_reversal", r.key, `Repeat reversals — ${r.label}`)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {repeatReversals.length === 0 && (
              <EmptyRow colSpan={3} label={`No contractor has reached ${MIN_SAMPLE_FOR_DISPLAY}+ reversals in this range.`} />
            )}
          </TableBody>
        </Table>
      </Section>

      {/* Negative override outcomes */}
      <Section
        title="Negative override outcomes"
        subtitle={`Planning overrides re-reviewed and judged negative in ${RANGE_LABEL[range].toLowerCase()}`}
        onExport={negativeOverrides.length ? exportOverrides : undefined}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px]">When</TableHead>
              <TableHead className="text-[11px]">Job</TableHead>
              <TableHead className="text-[11px]">Reviewer</TableHead>
              <TableHead className="text-[11px]">Reason</TableHead>
              <TableHead className="text-[11px] text-right">Review</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {negativeOverrides.map((o, i) => {
              const ackKey = `${o.jobId}:${o.observationId}`;
              const acked = isAcknowledged(ackIndex, "override_outcome", ackKey);
              return (
                <TableRow key={`${o.observationId}-${i}`}>
                  <TableCell className="text-[11px] whitespace-nowrap text-muted-foreground">
                    {format(new Date(o.recordedAt), "MMM d, h:mm a")}
                  </TableCell>
                  <TableCell className="text-xs font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-primary"
                      onClick={() => onJumpToJob?.(o.jobId)}
                      title="Jump to Execution Timeline"
                    >
                      {o.jobId}
                      {onJumpToJob && <ExternalLink className="h-3 w-3" />}
                    </button>
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{o.reviewerId}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{o.reason}</TableCell>
                  <TableCell className="text-right">
                    <AckButton
                      acknowledged={acked}
                      onClick={() => openAck("override_outcome", ackKey, `Override on ${o.jobId}`)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {negativeOverrides.length === 0 && (
              <EmptyRow colSpan={5} label="No negative override outcomes in this range." />
            )}
          </TableBody>
        </Table>
      </Section>

      {/* Acknowledgement dialog */}
      <Dialog open={!!ackTarget} onOpenChange={(o) => { if (!o) { setAckTarget(null); setAckNote(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as reviewed</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Acknowledging <span className="font-medium text-foreground">{ackTarget?.label}</span>.
              This is a UX-only annotation — it does not change the underlying suggestion,
              dispute, or holdback.
            </p>
            <Input
              value={ackNote}
              onChange={(e) => setAckNote(e.target.value.slice(0, 280))}
              placeholder="Optional note (≤280 chars, no $ or emails)"
              className="text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Reviewer: {reviewerId} • Append-only • Future occurrences of this contractor / suggestion
              kind will still appear.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAckTarget(null); setAckNote(""); }}>Cancel</Button>
            <Button onClick={submitAck}>Mark reviewed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
